import { motion } from "framer-motion";
import { Flame } from "lucide-react";

/**
 * Mini Duolingo-style streak strip — 7 cells = last 7 days (left → right, oldest → today).
 * - `days`: array from /me/stats `activity_7d` shape:
 *   { day: "Lun", date: "2026-04-26", day_num: 26, value, actions, active, is_today }
 * - `streak`: current consecutive-active-days count from `user.streak_days`
 *
 * Visual:
 *   - Active day = orange→yellow filled cell with flame on it
 *   - Inactive day = muted glass cell with the day letter
 *   - Today gets a pulsing ring + larger
 */
export default function StreakTimeline({ days, streak = 0 }) {
    if (!Array.isArray(days) || days.length === 0) return null;

    const activeCount = days.filter((d) => d.active).length;

    return (
        <div
            className="glass-card rounded-3xl p-4 sm:p-5 border border-white/5"
            data-testid="streak-timeline"
        >
            <div className="flex items-end justify-between gap-3 mb-3 flex-wrap">
                <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-9 h-9 rounded-2xl bg-shariti-orange/15 flex items-center justify-center shrink-0">
                        <Flame className="w-4 h-4 text-shariti-orange" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-shariti-orange-light">
                            Ta semaine
                        </p>
                        <p className="text-base sm:text-lg font-extrabold text-white leading-tight">
                            {streak > 0 ? (
                                <>
                                    {streak} jour{streak > 1 ? "s" : ""} de série
                                </>
                            ) : (
                                <>Lance ta série aujourd'hui</>
                            )}
                        </p>
                    </div>
                </div>
                <p className="text-[10px] uppercase tracking-widest font-bold text-white/40 shrink-0">
                    {activeCount}/7 jours actifs
                </p>
            </div>

            <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
                {days.map((d, i) => {
                    const letter = (d.day || "").charAt(0).toUpperCase();
                    return (
                        <motion.div
                            key={d.date || i}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.04 }}
                            className="flex flex-col items-center gap-1.5"
                            data-testid={`streak-cell-${i}`}
                        >
                            <span
                                className={`text-[10px] font-bold uppercase tracking-widest ${
                                    d.is_today
                                        ? "text-shariti-orange-light"
                                        : "text-white/40"
                                }`}
                            >
                                {letter}
                            </span>
                            <div
                                className={`relative w-full aspect-square rounded-xl flex items-center justify-center transition-all ${
                                    d.active
                                        ? "bg-gradient-to-br from-shariti-yellow to-shariti-orange shadow-[0_4px_24px_rgba(238,114,46,0.45)]"
                                        : "bg-white/5 border border-white/5"
                                } ${
                                    d.is_today
                                        ? "ring-2 ring-shariti-orange-light ring-offset-2 ring-offset-shariti-deep-black"
                                        : ""
                                }`}
                            >
                                {d.active ? (
                                    <Flame className="w-4 h-4 sm:w-5 sm:h-5 text-shariti-black" />
                                ) : (
                                    <span
                                        className={`text-xs sm:text-sm font-extrabold ${
                                            d.is_today
                                                ? "text-shariti-orange-light"
                                                : "text-white/25"
                                        }`}
                                    >
                                        {d.day_num ?? "·"}
                                    </span>
                                )}
                                {d.is_today && (
                                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-shariti-orange animate-ping opacity-75" />
                                )}
                            </div>
                        </motion.div>
                    );
                })}
            </div>

            <p className="text-[10px] text-white/35 mt-3 leading-relaxed italic">
                Une journée est validée dès que tu fais une action qui rapporte
                des points (quiz, simulateur, engagement…).
            </p>
        </div>
    );
}
